/**
 * ShowroomListHeader Theme
 *
 * Styles for the showroom listing page header component
 * Features hero banner with title and country links with count badges
 */

/** CF-032 video no-content — mobile full-width natural ratio; desktop unchanged */
const showroomListHeaderVideoNoContentSx = {
  '@media screen and (max-width: 991px)': {
    '&.showroom-list-header--video-no-content': {
      minHeight: 'auto',
    },
    '&.showroom-list-header--video-no-content .showroom-list-header__video-media': {
      bottom: 'auto',
      height: 'auto',
      left: 0,
      position: 'relative',
      right: 'auto',
      top: 'auto',
      width: '100%',
    },
    '&.showroom-list-header--video-no-content .hero-banner__video-layer--mobile': {
      height: 'auto !important',
      width: '100% !important',
    },
    '&.showroom-list-header--video-no-content .hero-banner__video-layer--mobile .component--video': {
      height: 'auto !important',
      width: '100% !important',
    },
    '&.showroom-list-header--video-no-content .hero-banner__video-layer--mobile .amb-video': {
      height: 'auto !important',
      width: '100% !important',
    },
    '&.showroom-list-header--video-no-content .hero-banner__video-layer--mobile video, &.showroom-list-header--video-no-content .hero-banner__video-layer--mobile iframe': {
      display: 'block',
      height: 'auto',
      objectFit: 'contain',
      position: 'relative',
      width: '100%',
    },
  },
};

/**
 * Get base container styles for the showroom list header.
 * Background images are applied responsively in the component via sx.
 * @returns {Object} Container styles
 */
export const getContainerStyles = () => ({
  position: 'relative',
  width: '100%',
  minHeight: {base: '320px', md: '400px', lg: '500px'},
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  overflow: 'hidden',
  sx: showroomListHeaderVideoNoContentSx,
});

const ShowroomListHeader = {
  parts: [
    'container',
    'overlay',
    'content',
    'title',
    'countryLinksContainer',
    'countryLink',
    'countryLinkActive',
    'countryLinkDisabled',
    'countBadge',
    'contentWrapper',
    'videoMedia',
  ],
  baseStyle: {
    container: {
      position: 'relative',
      width: '100%',
      minHeight: {base: '320px', md: '400px', lg: '500px'},
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      overflow: 'hidden',
    },
    videoMedia: {
      bottom: 0,
      height: '100%',
      left: 0,
      overflow: 'hidden',
      position: 'absolute',
      right: 0,
      top: 0,
      width: '100%',
      zIndex: 0,
      sx: {
        '.hero-banner__video-layer': {
          height: '100%',
          width: '100%',
        },
        '.component--video': {
          height: '100%',
          width: '100%',
        },
        '.amb-video': {
          aspectRatio: 'unset',
          height: '100%',
          width: '100%',
        },
        'img, video, iframe': {
          height: '100%',
          objectFit: 'cover',
          width: '100%',
        },
      },
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bg: 'blackAlpha.400',
      zIndex: 1,
    },
    content: {
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
      textAlign: 'left',
      width: '100%',
      height: '100%',
    },
    title: {
      color: 'white',
      fontFamily: 'heading',
      fontSize: {base: '40px', lg: '72px'},
      fontWeight: '400',
      lineHeight: '120%',
      letterSpacing: {base: '-0.4px', lg: '-0.72px'},
    },
    countryLinksContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: {base: '8px', lg: '24px'},
      rowGap: {base: '8px', lg: '12px'},
    },
    countryLink: {
      display: 'inline-flex',
      alignItems: 'baseline',
      color: 'white',
      cursor: 'pointer',
      fontFamily: 'body',
      fontSize: {base: 'md', lg: 'lg'},
      fontWeight: 'medium',
      lineHeight: 'shorter',
      textDecoration: 'none',
      height: 'auto',
      minWidth: 0,
      padding: 0,
      transition: 'color 0.2s ease-in-out',
      appearance: 'none',
      background: 'transparent',
      border: 'none',
      borderRadius: 0,
      _hover: {
        color: 'white',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
      },
      _focus: {
        outline: 'none',
      },
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'whiteAlpha.800',
        outlineOffset: '2px',
      },
    },
    countryLinkActive: {
      color: 'brandColors.primaryLinen100',
      cursor: 'default',
      fontWeight: 'medium',
      textDecoration: 'none',
      _hover: {
        color: 'brandColors.primaryLinen100',
        textDecoration: 'none',
      },
    },
    countryLinkDisabled: {
      color: 'white',
      cursor: 'default',
      textDecoration: 'none',
      _hover: {
        color: 'white',
        textDecoration: 'none',
      },
    },
    countBadge: {
      color: 'whiteAlpha.500',
      fontSize: {base: '2xs', lg: 'xs'},
      fontWeight: 'light',
      lineHeight: 'shorter',
      ml: '1px',
      verticalAlign: 'super',
    },
    contentWrapper: {
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
      gap: {base: '26px', lg: '44px'},
      width: '100%',
      maxWidth: '1440px',
      mx: 'auto',
      paddingX: {base: '20px', lg: '80px'},
      paddingBottom: {base: '40px', lg: '70px'},
    },
  },
};

export default ShowroomListHeader;
